import { Link, usePath } from "@/router";

/*
 * The section links are in-page scroll targets, so they only mean anything on
 * the home page. On /contact they are dropped rather than rewritten to
 * `/#mission`, which would work but would put the hash back in the address
 * bar — the thing SmoothScroll exists to avoid. A visitor on the contact page
 * gets the brand as the way back instead, which is where they would look.
 *
 * There is no "Contact" link beside them, on purpose. It pointed at exactly
 * the same page as the button next to it, so the nav offered one destination
 * twice — and on /contact it offered the page you were already standing on.
 * The button is the louder of the two and the one that reads as an action, so
 * it is the one that survived. Below 940px the links hide anyway and the
 * button was always the only way through, so this makes the wide layout agree
 * with the narrow one rather than inventing a new rule.
 */
const SECTIONS = [
  { href: "#mission", label: "Mission" },
  { href: "#how", label: "How we work" },
  { href: "#services", label: "Services" },
];

export function Nav() {
  const path = usePath();
  const onHome = path === "/";

  /*
   * A call to action pointing at the page you are reading is furniture, not a
   * call to action. It stays on the outcome pages and on the 404, where it is
   * a genuine way back into the funnel, and disappears only here.
   */
  const onContact = path === "/contact";

  return (
    <header className="k-nav">
      <div className="k-nav__in">
        {/*
          `Link` rather than an anchor even for the brand, so returning from
          /contact is a client transition and the nav never repaints.
        */}
        <Link className="k-brand" href="/">
          <span className="k-brand__mark" aria-hidden="true">
            <KeyMark />
          </span>
          <span>
            Keglam <span className="k-brand__sep">·</span> Services
          </span>
        </Link>

        {/*
          Rendered only when it has something in it. An empty <nav> with an
          aria-label is a landmark a screen reader announces and then finds
          nothing inside.
        */}
        {onHome && (
          <nav className="k-nav__links" aria-label="Sections">
            {SECTIONS.map((section) => (
              <a key={section.href} href={section.href}>
                {section.label}
              </a>
            ))}
          </nav>
        )}

        {!onContact && (
          <Link className="k-btn" href="/contact">
            Book an hour
          </Link>
        )}
      </div>
    </header>
  );
}

/* A key, for Keglam — drawn rather than imported so the whole page ships
   without an icon dependency for the sake of one mark. */
function KeyMark() {
  return (
    <svg width="19" height="19" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <g stroke="#14181a" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="8.5" cy="8.5" r="4.5" />
        <path d="M11.8 11.8 20 20" />
        <path d="M17 17.2 15 19.2" />
      </g>
    </svg>
  );
}
